import { BASE_URL } from '@/environment/environment'
import { looksLikePersonName } from '@/modules/chat-matrix-fast/constants/chat'
import { MATRIX_EMPLOYEE_QUERY_PARAMS } from '@/modules/chat-matrix-fast/constants/employeeApi'
import { httpGet } from '@/modules/chat-matrix-fast/services/httpClient'
import { findEmployeeInTreeByMatrixUserId } from '@/modules/chat-matrix-fast/utils/employeeTree'
import { normalizeAvatarUrl } from '@/modules/chat-matrix-fast/utils/userDataExtractor'
import type { IEmployeeTreeDepartment, IEmployeeTreeItem } from '@/modules/apiStore/types/employees/IEmployeeTree'

const employeeIdByMatrixUserId = new Map<string, string>()
const employeeNameByMatrixUserId = new Map<string, string>()
const employeeNameById = new Map<string, string>()
const avatarByEmployeeId = new Map<string, string | null>()
const avatarPendingByEmployeeId = new Map<string, Promise<string | null>>()
let employeesTreePromise: Promise<IEmployeeTreeDepartment[]> | null = null

function indexEmployee(employee: IEmployeeTreeItem): void {
  const name = employee.full_name?.trim()
  const id = String(employee.id || '').trim()
  const mxid = employee.matrix_user_id?.trim()

  if (name && id) {
    employeeNameById.set(id, name)
  }
  if (name && mxid) {
    employeeNameByMatrixUserId.set(mxid, name)
  }
  if (id && mxid) {
    employeeIdByMatrixUserId.set(mxid, id)
  }
}

function indexEmployeesTree(departments: IEmployeeTreeDepartment[]): void {
  for (const department of departments) {
    for (const employee of department.employees ?? []) {
      indexEmployee(employee)
    }
    if (department.children?.length) {
      indexEmployeesTree(department.children)
    }
  }
}

/** Дерево сотрудников (кэшируется на сессию, единая точка запроса) */
export async function loadEmployeesTree(): Promise<IEmployeeTreeDepartment[]> {
  if (!employeesTreePromise) {
    employeesTreePromise = httpGet<{ results: IEmployeeTreeDepartment[] }>(
      `${BASE_URL}/api/employee/employees/tree/`,
      { params: { fields: MATRIX_EMPLOYEE_QUERY_PARAMS.fields } },
    )
      .then(data => {
        const tree = data.results ?? []
        indexEmployeesTree(tree)
        return tree
      })
      .catch(() => {
        employeesTreePromise = null
        return []
      })
  }

  return employeesTreePromise
}

/** Кладём ФИО из dialog_member чата, чтобы не ходить в дерево сотрудников. */
export function indexEmployeeDisplayName(options: {
  id?: string | null
  matrixUserId?: string | null
  fullName?: string | null
}): void {
  const name = options.fullName?.trim()
  if (!name || !looksLikePersonName(name)) {
    return
  }

  const id = String(options.id || '').trim()
  const mxid = options.matrixUserId?.trim()

  if (id) {
    employeeNameById.set(id, name)
  }
  if (mxid) {
    employeeNameByMatrixUserId.set(mxid, name)
  }
  if (id && mxid) {
    employeeIdByMatrixUserId.set(mxid, id)
  }
}

export function getCachedEmployeeDisplayName(options: {
  matrixUserId?: string | null
  employeeId?: string | null
}): string | undefined {
  const mxid = options.matrixUserId?.trim()
  if (mxid) {
    const fromMxid = employeeNameByMatrixUserId.get(mxid)
    if (fromMxid) {
      return fromMxid
    }
  }

  const employeeId = String(options.employeeId || '').trim()
  if (employeeId) {
    return employeeNameById.get(employeeId)
  }

  return undefined
}

/** UUID сотрудника по Matrix MXID (@dao_…:server) */
export async function resolveEmployeeIdByMatrixUserId(
  matrixUserId: string,
): Promise<string | undefined> {
  const normalized = matrixUserId.trim()
  if (!normalized) {
    return undefined
  }

  const cached = employeeIdByMatrixUserId.get(normalized)
  if (cached) {
    return cached
  }

  const tree = await loadEmployeesTree()
  const found = findEmployeeInTreeByMatrixUserId(tree, normalized)
  if (!found?.id) {
    return undefined
  }

  indexEmployee(found)
  return String(found.id)
}

async function fetchEmployeeAvatar(employeeId: string): Promise<string | null> {
  if (avatarByEmployeeId.has(employeeId)) {
    return avatarByEmployeeId.get(employeeId) ?? null
  }

  const pending = avatarPendingByEmployeeId.get(employeeId)
  if (pending) {
    return pending
  }

  const promise = (async (): Promise<string | null> => {
    try {
      const employee = await httpGet<{ avatar?: string | null }>(
        `${BASE_URL}/api/employee/employees/${employeeId}/`,
      )
      const avatar = normalizeAvatarUrl(employee.avatar)
      avatarByEmployeeId.set(employeeId, avatar)
      return avatar
    } catch {
      avatarByEmployeeId.set(employeeId, null)
      return null
    } finally {
      avatarPendingByEmployeeId.delete(employeeId)
    }
  })()

  avatarPendingByEmployeeId.set(employeeId, promise)
  return promise
}

/** Аватар собеседника из профиля сотрудника (без POST /dialog/) */
export async function resolvePeerAvatarFromBackend(
  matrixUserId: string,
): Promise<string | null> {
  const employeeId = await resolveEmployeeIdByMatrixUserId(matrixUserId)
  if (!employeeId) {
    return null
  }

  return fetchEmployeeAvatar(employeeId)
}
